import React from "react";
import { act, render, waitFor } from "@testing-library/react";
import { useChatStore } from "@metalmind-ab/mmagent-chat-ui";
import { ChatPanel } from "./ChatPanel";

const mockProviderConfigs: Array<Record<string, unknown>> = [];
jest.mock("@metalmind-ab/mmagent-chat-ui", () => {
  const actual = jest.requireActual("@metalmind-ab/mmagent-chat-ui");
  return {
    ...actual,
    ChatProvider: (props: { config: Record<string, unknown> }) => {
      mockProviderConfigs.push(props.config);
      return actual.ChatProvider(props);
    },
  };
});

let mockGetHeaders = async () => ({ Authorization: "test-token" });
jest.mock("@metalmind-ab/mmagent-chat-ui/styles.css", () => ({}), {
  virtual: true,
});
jest.mock("../../../../hooks/useAuthHeaders", () => ({
  useAuthHeaders: () => mockGetHeaders,
}));
jest.mock("../../../../hooks/useChatImageAttachments", () => ({
  useChatImageAttachments: () => ({
    enabled: false,
    uploadImage: jest.fn(),
  }),
}));
jest.mock("../../../../config/runtimeConfig", () => ({
  getApiBaseUrl: () => "http://localhost",
}));
jest.mock("../../../../utils/chatResumeOptions", () => ({
  buildChatResumeOptions: () => ({}),
}));

const originalFetch = global.fetch;

beforeEach(() => {
  mockProviderConfigs.length = 0;
  useChatStore.getState().clearMessages();
  useChatStore.getState().setConversationId(null);
  useChatStore.getState().setConversations([], 0);
  Object.defineProperty(HTMLElement.prototype, "scrollIntoView", {
    configurable: true,
    value: jest.fn(),
  });
});

afterEach(() => {
  global.fetch = originalFetch;
  mockGetHeaders = async () => ({ Authorization: "test-token" });
});

it("sends the canvas namespace with the conversation list and provider requests", async () => {
  const fetchMock = jest.fn(
    async (_url: RequestInfo | URL, _init?: RequestInit) =>
      ({
        ok: true,
        json: async () => ({ items: [], total: 0 }),
      }) as Response,
  );
  global.fetch = fetchMock as unknown as typeof fetch;

  render(<ChatPanel workspaceId="workspace-1" canvasId="canvas-7" />);

  await waitFor(() => expect(mockProviderConfigs.length).toBeGreaterThan(0));
  const providerHeaders = mockProviderConfigs[0].headers as Record<
    string,
    string
  >;
  expect(providerHeaders["X-MyVault-Canvas-Id"]).toBe("canvas-7");
  expect(providerHeaders["X-Workspace-Id"]).toBe("workspace-1");
  await waitFor(() =>
    expect(
      fetchMock.mock.calls.some(([url]) =>
        String(url).includes("/chat/conversations?"),
      ),
    ).toBe(true),
  );
  const listRequest = fetchMock.mock.calls.find(([url]) =>
    String(url).includes("/chat/conversations?"),
  );
  const listHeaders = listRequest?.[1]?.headers as
    Record<string, string> | undefined;
  expect(listHeaders?.["X-MyVault-Canvas-Id"]).toBe("canvas-7");
});

it("clears the shared conversation store when switching between workspace and canvas assistants", async () => {
  global.fetch = jest.fn(
    async () =>
      ({ ok: true, json: async () => ({ items: [], total: 0 }) }) as Response,
  ) as unknown as typeof fetch;

  const { rerender } = render(<ChatPanel workspaceId="workspace-1" />);
  await waitFor(() => expect(mockProviderConfigs.length).toBeGreaterThan(0));
  act(() => useChatStore.getState().setConversationId(101));

  rerender(<ChatPanel workspaceId="workspace-1" canvasId="canvas-7" />);

  await waitFor(() =>
    expect(
      mockProviderConfigs.some(
        (config) =>
          (config.headers as Record<string, string>)["X-MyVault-Canvas-Id"] ===
          "canvas-7",
      ),
    ).toBe(true),
  );
  expect(useChatStore.getState().conversationId).toBeNull();
});
